import React, { FunctionComponent } from 'react';
import { defaults } from 'lodash';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { Dealer } from '../../../types/dealerTypes';
import styles from '../styles.module.scss';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import { addDeskingDefaults } from '../defaults';
import { Radio } from '../../Input/Radio/Radio';

export const DealerLaunchLeaseInceptionFees: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const defaultDealer = addDeskingDefaults(dealer);
  const form = useLaunchpadForm<Dealer>({
    defaultValues: defaultDealer,
  });

  const onSubmit = async () => {
    let addInceptionFeesToTotalAtStart = false;
    const customerToggleEnabled =
      dealer.desking?.inceptionFeesTotalAtStart?.customerToggle?.enabled ||
      false;
    const enabledByDefault =
      dealer.desking?.inceptionFeesTotalAtStart?.enabledByDefault || true;

    const { desking } = form.getValues();
    const { inceptionFeesTotalAtStart } = desking || {};

    const isTaxesEnabled = inceptionFeesTotalAtStart?.taxes?.enabled || false;
    const isFeesEnabled = inceptionFeesTotalAtStart?.fees?.enabled || false;
    const isFirstMonthEnabled =
      inceptionFeesTotalAtStart?.firstMonth?.enabled || false;

    if (!isTaxesEnabled && !isFeesEnabled && !isFirstMonthEnabled) {
      addInceptionFeesToTotalAtStart = false;
    } else {
      addInceptionFeesToTotalAtStart = true;
    }

    const partialDesking = {
      ...dealer.desking,
      addInceptionFeesToTotalAtStart,
      inceptionFeesTotalAtStart: {
        ...dealer?.desking?.inceptionFeesTotalAtStart,
        taxes: {
          enabled: isTaxesEnabled,
        },
        fees: {
          enabled: isFeesEnabled,
        },
        firstMonth: {
          enabled: isFirstMonthEnabled,
        },
        customerToggle: {
          enabled: customerToggleEnabled,
        },
        enabledByDefault,
      },
      includeTaxInCapCost: !isTaxesEnabled,
    };
    // eslint-disable-next-line
    await nextStep({
      // eslint-disable @typescript-eslint/no-redeclare.
      desking: defaults(
        { ...partialDesking },
        { enabled: true, defaultPencil: false },
      ),
    });
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.widgetsStep}
      form={form}
      title="Tell Us About How Your Payments Are Configured"
      subTitle=""
      cardTitle="Should inception fees be added to start or cap cost for leases?"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <h2>Taxes:</h2>
      <Radio
        className={styles.inceptionFeesGrid}
        name={'desking.inceptionFeesTotalAtStart.taxes.enabled' as never}
        options={[
          { label: 'Up Front', value: true },
          {
            label: 'In Cap Cost',
            value: false,
          },
        ]}
        form={form}
        validation={{
          validate: (val: any) => {
            if (typeof val !== 'boolean') return 'A selection is required.';
            return true;
          },
        }}
      />

      <h2>
        Fees: <TooltipIcon content="Government and DMV fees" />
      </h2>
      <Radio
        className={styles.inceptionFeesGrid}
        name={'desking.inceptionFeesTotalAtStart.fees.enabled' as never}
        options={[
          { label: 'Up Front', value: true },
          {
            label: 'In Cap Cost',
            value: false,
          },
        ]}
        form={form}
        validation={{
          validate: (val: any) => {
            if (typeof val !== 'boolean') return 'A selection is required.';
            return true;
          },
        }}
      />

      {/* eslint-disable-next-line react/no-unescaped-entities */}
      <h2>First Month's Payment:</h2>
      <Radio
        className={styles.inceptionFeesGrid}
        name={'desking.inceptionFeesTotalAtStart.firstMonth.enabled' as never}
        options={[
          {
            label: 'Due at Start',
            value: true,
          },
          {
            label: 'Not Due at Start',
            value: false,
          },
        ]}
        form={form}
        validation={{
          validate: (val: any) => {
            if (typeof val !== 'boolean') return 'A selection is required.';
            return true;
          },
        }}
      />
    </DealerLaunchFormCardLayout>
  );
};
